import { useState, useRef } from "react";
import { useQueryClient } from "react-query";

const areEqual = (left, right) =>
  JSON.stringify(left ?? null) === JSON.stringify(right ?? null);

export default function useEditMode({
  editModeEnabled,
  updateMutation,
  initialQueryKey,
  specificUpdateKey,
}) {
  const [editedObject, setEditedObject] = useState(null);
  const previousData = useRef(null);
  const [editMode, setEditMode] = useState(editModeEnabled);
  const queryClient = useQueryClient();
  const queryKey = useRef(initialQueryKey);

  const setQueryKey = (_queryKey) => {
    queryKey.current = [..._queryKey];
  };

  const mergeEditState = (prevState, patch) => {
    if (specificUpdateKey !== undefined) {
      return {
        ...prevState,
        [specificUpdateKey]: {
          ...prevState[[specificUpdateKey]],
          ...patch,
        },
      };
    }
    return { ...prevState, ...patch };
  };

  const updateEditState = (id, val) => {
    setEditedObject((prevState) => {
      const updatedState = mergeEditState(prevState, { [id]: val });
      updateQueryData(updatedState);
      return updatedState;
    });
  };

  const updateEditStateMany = (patch) => {
    setEditedObject((prevState) => {
      const updatedState = mergeEditState(prevState, patch);
      updateQueryData(updatedState);
      return updatedState;
    });
  };

  const enterEditMode = (id) => {
    setEditMode(id);
    const prevData = queryClient.getQueryData([...queryKey.current]);
    previousData.current =
      prevData === undefined
        ? {
            id: [...queryKey.current].find((el) => typeof el === "number"),
          }
        : prevData;
    setEditedObject(previousData.current);
  };

  const cancelEditMode = () => {
    setEditMode(false);
    setEditedObject(previousData.current);
    restorePreviousData();
  };

  const confirmEditMode = () => {
    setEditMode(false);
    if (areEqual(editedObject, previousData.current)) {
      return;
    }
    updateMutation.mutate(editedObject, {
      onError: () => {
        restorePreviousData();
      },
    });
  };

  const updateQueryData = (updatedState) => {
    queryClient.setQueryData(queryKey.current, (oldData) => {
      return {
        ...oldData,
        ...updatedState,
      };
    });
  };

  const restorePreviousData = () => {
    queryClient.invalidateQueries(queryKey.current);

    // might be useful for queries with "enabled" since they dont get refreshed
    queryClient.setQueryData(queryKey.current, previousData.current);
  };

  return {
    editMode,
    enterEditMode,
    cancelEditMode,
    confirmEditMode,
    updateEditState,
    updateEditStateMany,
    setQueryKey,
  };
}
